/**
 * SchlagwortFeld — die EINE Bedienung für die Schlagworte („Tags") eines Werks.
 *
 * ## Warum es dieses Bauteil gibt
 *
 * Bis zum 2026-09-08 stand derselbe Block ZWEIMAL von Hand im Baum, in `AudioForm` und
 * `VideoForm`. Beim Heben nachweislich zeichengleich — beide Blöcke extrahiert, Kommentare
 * geleert, gediffed: Exit 0; Positivkontrolle mit EINEM geänderten Zeichen (`px-3` → `px-4`):
 * Exit 1. Der einzige Unterschied war ein längerer Kommentar über der Beschriftung.
 *
 * Der Anlass war nicht Ordnungsliebe: die MindBook-Detailseite zeigt seit dem 2026-09-08 die
 * Schlagworte an (21 von 21 MindBooks tragen je fünf), aber das MindBook-FORMULAR hatte keine
 * Bedienung dafür — die Redaktion sah fünf Schlagworte je Werk, die sie nirgends anfassen
 * konnte. Der dritte Handstand wäre die naheliegende, falsche Antwort gewesen.
 *
 * ⛔ **`WebComponentForm` ist bewusst NICHT mitgehoben.** Sein Tag-Block sieht ähnlich aus, ist
 * aber gemessen ANDERS: `gap-1 px-2 py-1`, `bg-secondary-100`, `rounded` statt `rounded-full`.
 * Ihn mitzunehmen wäre eine sichtbare Änderung auf einer vierten Fläche, die niemand entschieden
 * hat — und genau die Sorte „still gewonnene" Angleichung, die man beim Heben nicht macht,
 * sondern MELDET. Der Unterschied steht als eigener BACKLOG-Posten.
 *
 * ## Was hier bewusst NICHT wählbar ist
 *
 * Farben, Größen, Abstände, die Beschriftung „Tags", der Platzhalter. Die stehen einmal hier.
 * `werte` und `onÄndern` sind die ganze Schnittstelle: das Bauteil hält KEINEN eigenen Zustand
 * über die Liste — sonst gäbe es zwei Wahrheiten, die des Formulars und die des Feldes.
 *
 * ⚠️ Der Eingabetext dagegen IST lokaler Zustand und gehört hierher: er ist unfertige Eingabe,
 * kein Wert des Werks. In `AudioForm`/`VideoForm` lag er in einem `useRef` am Formular — das
 * war der Grund, warum jede Vorlage die Funktionen `addTag`/`removeTag` selbst schreiben musste.
 */

import React, { useId, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface SchlagwortFeldProps {
  /** Die Schlagworte des Werks. Der Besitzer ist das Formular, nicht dieses Bauteil. */
  werte: string[];
  /** Bekommt die NEUE Liste — nie eine Mutation der alten. */
  onÄndern: (neu: string[]) => void;
}

export const SchlagwortFeld: React.FC<SchlagwortFeldProps> = ({ werte, onÄndern }) => {
  const feldId = useId();
  const [eingabe, setEingabe] = useState('');

  /**
   * ⚠️ Die Doppelbedingung ist aus den gehobenen Vorlagen übernommen, nicht neu erfunden:
   * `trim()` gegen Leereingaben, `includes` gegen Dubletten. Ohne die zweite legt ein zweiter
   * Klick denselben Wert erneut an, und die Liste zeigt ihn doppelt — sie ist die einzige
   * Prüfung, die es gibt.
   */
  const hinzufügen = () => {
    const wert = eingabe.trim();
    if (!wert || werte.includes(wert)) return;
    onÄndern([...werte, wert]);
    setEingabe('');
  };

  const entfernen = (wert: string) => onÄndern(werte.filter((t) => t !== wert));

  return (
    <div>
      {/* Die Beschriftung gehört dem Eingabefeld unten, nicht der Merkmal-Liste darüber: dort
          stehen nur Entfernen-Schaltflächen, die ihren Text selbst tragen. */}
      <label htmlFor={`${feldId}-tag`} className="block text-sm font-medium text-secondary-700 mb-2">
        Tags
      </label>
      <div className="flex flex-wrap gap-2 mb-2">
        {werte.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
          >
            {tag}
            <button
              type="button"
              onClick={() => entfernen(tag)}
              aria-label={`Tag „${tag}" entfernen`}
              className="ml-2 text-primary-500 hover:text-primary-700"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex space-x-2">
        <input
          id={`${feldId}-tag`}
          type="text"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          // ⚠️ `onKeyDown`, nicht `onKeyPress`: das gehobene Original benutzte `onKeyPress`, und
          // das ist in React wie im DOM abgekündigt. Verhalten unverändert — Enter fügt hinzu und
          // schickt das Formular NICHT ab (`preventDefault` steht davor, wie im Original).
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            hinzufügen();
          }}
          className="input flex-1"
          placeholder="Tag hinzufügen..."
        />
        <button type="button" onClick={hinzufügen} aria-label="Tag hinzufügen" className="btn btn-outline">
          <CheckIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
